/**
 * Shared aggregator — pulls compact summaries for every banker-pushed journey
 * this device has opened. Powers Home, My Financial Life, My Documents,
 * My Trusted Institutions, and My Consumer Record.
 */
import { useEffect, useState, useMemo } from "react";
import axios from "axios";
import { listJourneyTokens } from "@/lib/wallet-storage";

const API = `${process.env.REACT_APP_BACKEND_URL || ""}/api`;

export function useWalletJourneys() {
  const [rows, setRows] = useState(null);
  const [error, setError] = useState(null);
  const tokens = useMemo(() => listJourneyTokens().map(t => t.token), []);

  useEffect(() => {
    if (tokens.length === 0) { setRows([]); return; }
    axios.post(`${API}/j/bulk-summary`, { tokens })
      .then(r => setRows(r.data || []))
      .catch(e => setError(e?.response?.data?.detail || "Could not load your reviews."));
  }, [tokens]);

  return { rows, error, tokens };
}

/**
 * Derived view models — computed once, memoised.
 */
export function useWalletDerived() {
  const { rows, error, tokens } = useWalletJourneys();
  const view = useMemo(() => {
    if (!rows) return null;
    const active = rows.filter(r => r.status === "completed" && r.review_status === "interested");
    const considering = rows.filter(r => r.status !== "completed" && r.review_status !== "not_interested");
    const declined = rows.filter(r => r.review_status === "not_interested");
    const upcoming = rows.filter(r => r.status !== "completed" && r.percent_complete < 100);

    // Decisions — chronological ledger of every real decision the consumer recorded.
    const DECISION_STATUSES = new Set(["interested", "not_interested", "decision_recorded", "need_more_info"]);
    const decisions = rows
      .filter(r => DECISION_STATUSES.has(r.review_status) || r.status === "completed")
      .map(r => ({
        ...r,
        decision_at_ms: r.decided_at ? new Date(r.decided_at).getTime()
                       : r.delivered_at ? new Date(r.delivered_at).getTime()
                       : 0,
      }))
      .sort((a, b) => b.decision_at_ms - a.decision_at_ms);

    // Institutions unique-keyed by (institution + advisor) — bank + branch shows as one card
    const instMap = new Map();
    for (const r of rows) {
      const key = (r.institution || r.advisor_name || "Institution").trim();
      const cur = instMap.get(key) || {
        key, institution: r.institution, advisor_name: r.advisor_name,
        product_count: 0, journey_count: 0, last_seen: null, journeys: [], product_types: new Set(),
      };
      cur.journey_count += 1;
      cur.journeys.push(r);
      if (r.product_type) cur.product_types.add(r.product_type);
      const seen = r.delivered_at ? new Date(r.delivered_at).getTime() : 0;
      if (!cur.last_seen || seen > cur.last_seen) cur.last_seen = seen;
      instMap.set(key, cur);
    }
    const institutions = Array.from(instMap.values())
      .map(x => ({ ...x, product_types: Array.from(x.product_types), product_count: x.product_types.size || x.journey_count }))
      .sort((a, b) => (b.last_seen || 0) - (a.last_seen || 0));

    return { active, considering, declined, upcoming, decisions, institutions };
  }, [rows]);

  return { rows, error, tokens, view };
}
